import { useCallback } from 'react';

import { ContentAndHeaderQuery } from '@confluence/content-unified-query';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { SpaceOverviewQuery } from '@confluence/space-overview-query';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId';
import { ContentModeQuery } from '@confluence/editor-content-mode-button/entry-points/ContentModeQuery.graphql';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

export const useForceRefetchContent = (contentId: string, spaceKey: string) => {
	const homepageId = useSpaceHomepageId(spaceKey);

	return useCallback(async () => {
		let spaceOverviewQueryResponse;
		let contentAndHeaderQueryResponse;
		let contentModeQueryResponse;
		if (contentId === homepageId) {
			try {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				spaceOverviewQueryResponse = await getApolloClient().query({
					query: SpaceOverviewQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						spaceKey,
						excludeOperations: Boolean(!isCompanyHubSpaceKey(spaceKey)),
					},
				});
			} catch (error) {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}
		}
		if (contentId) {
			try {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				contentAndHeaderQueryResponse = await getApolloClient().query({
					query: ContentAndHeaderQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						contentId,
						spaceKey,
						useNewContentTopper: true,
						...(fg('confluence_frontend_publish_livedoc_refetch_fix')
							? { cacheBuster: Date.now() }
							: {}),
					},
				});
			} catch (error) {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}

			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
				fg('platform_editor_content_mode_button_mvp')
			) {
				try {
					// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
					contentModeQueryResponse = await getApolloClient().query({
						query: ContentModeQuery,
						context: { single: true },
						fetchPolicy: 'network-only',
						variables: {
							contentId,
							isDraft: false,
							status: ['current'],
						},
					});
				} catch (error) {
					// NOTE: Content mode query is best-effort; swallow errors to keep refetch resilient.
				}
			}
		}
		return { spaceOverviewQueryResponse, contentAndHeaderQueryResponse, contentModeQueryResponse };
	}, [contentId, spaceKey, homepageId]);
};
